import { BASE_LEVEL } from "@sins2-atlas/compute";
import { LEVEL_COUNT } from "@sins2-atlas/game-data";
import { asToken, joinSlots, routerValue, splitSlots, type UrlValue } from "./url-slots";

// The Level's URL grammar (CONTEXT.md: Level; ADR 0012, Issue 49), modelled on lib/loadout.ts
// and pure in the same way — no dataset import, so the rules are testable over plain values.
//
// A Level belongs to ONE hull in one place, an entity page or one comparison column, so like a
// Loadout it does NOT ride unit-level links: `?level=` is deliberately absent from
// lib/frame.ts's SerializedFrame, and that absence is what makes clicking to another hull start
// it at level 1. The Tech Set keeps riding links beside it, because research is something an
// empire has while a Level is something one ship earned.

export { BASE_LEVEL, LEVEL_COUNT };

/** A `?level=` value as it can arrive — the site's first NUMERIC URL key (lib/url-slots.ts,
 * UrlValue: a single level arrives as a NUMBER, a `;`-slotted one as a string). */
export type LevelValue = UrlValue;

/** One `?level=` slot: the game's own 1 to 10, or the token the URL named that is not a level.
 * A level is honoured only as a whole number in range — "0", "11", "3.5" and "ten" all name a
 * ship the game cannot field, so each falls back to level 1 and is reported rather than
 * silently rounded into something the reader did not ask for. */
export function parseLevelSlot(value: LevelValue | undefined): {
  level: number;
  unknown?: string;
} {
  const token = asToken(value);
  if (token === undefined || token === "") return { level: BASE_LEVEL };
  // Number() would accept " 3 ", "3e0" and "0x3"; the ladder is spelled in plain digits.
  const level = /^\d+$/.test(token) ? Number(token) : Number.NaN;
  if (!Number.isInteger(level) || level < BASE_LEVEL || level > LEVEL_COUNT) {
    return { level: BASE_LEVEL, unknown: token };
  }
  return { level };
}

/** The Level as its one slot value, or undefined at level 1 so a bare link stays canonical —
 * the rule Base and an uncorrupted frame already follow. A NUMBER, so the router writes
 * `?level=10` rather than the `?level=%2210%22` a string round-trips to: JSON.parse would read
 * a bare 10 back as a number, so the router quotes any string that looks like one. */
export function serializeLevel(level: number | undefined): number | undefined {
  return level === undefined || level === BASE_LEVEL ? undefined : level;
}

// The per-column layer (ADR 0011, lib/url-slots.ts): the one coercion the Level adds is
// LevelValue → string, so a number arriving from the router splits like any other slot.

export const splitLevelSlots = (value: LevelValue | undefined): string[] =>
  splitSlots(asToken(value));

/** The columns' slots as the one `?level=` value — each as serializeLevelSearch spells it, an
 * unrecognised token kept verbatim so the notice keeps quoting it — joined, and a single level
 * handed back as a NUMBER: the router stringifies with JSON, so a "10" would reach the URL as
 * ?level=%2210%22, while a slotted value ("10;;2") is no number and stays the string it is. The
 * whole duality is decided here, beside serializeLevel, rather than at the comparison's writer
 * (review 2026-09-08). */
export function joinLevelValues(
  slots: readonly (LevelValue | undefined)[],
): LevelValue | undefined {
  return routerValue(joinSlots(slots.map(asToken)));
}
